import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import {
  generateQuestions,
  normalizeWritten,
  snapToNearestHour12,
  snapToNearestValidMinute,
  timesMatch,
  VALID_MINUTES,
} from './HoursData'
import type { Difficulty, GameMode, HoursQuestion, HoursTimeTranslations, TimeValue, ValidMinute } from './HoursData'

export type { Difficulty, GameMode }

interface HoursState {
  questions:              HoursQuestion[]
  currentIdx:             number
  answered:               number
  moves:                  number
  // MCQ flash states (easy/medium)
  correctFlashId:         string | null   // written string or clock option id
  wrongId:                string | null   // written string or clock option id
  // Hard text input (non-reverse)
  inputValue:             string
  inputStatus:            'idle' | 'correct' | 'wrong'
  // Hard interactive clock (reverse)
  interactiveHour:        number
  interactiveMinute:      ValidMinute
  interactiveSubmitWrong: boolean
  // Lifecycle
  gameComplete:           boolean
  startTime:              number | null
  endTime:                number | null
}

function initInteractiveTime(mode: GameMode): { hour: number; minute: ValidMinute } {
  return { hour: mode === 'analog' ? 3 : 3, minute: 0 }
}

function initState(difficulty: Difficulty, mode: GameMode, isReverse: boolean, timeT: HoursTimeTranslations): HoursState {
  const { hour, minute } = initInteractiveTime(mode)
  return {
    questions:              generateQuestions(difficulty, mode, isReverse, timeT),
    currentIdx:             0,
    answered:               0,
    moves:                  0,
    correctFlashId:         null,
    wrongId:                null,
    inputValue:             '',
    inputStatus:            'idle',
    interactiveHour:        hour,
    interactiveMinute:      minute,
    interactiveSubmitWrong: false,
    gameComplete:           false,
    startTime:              null,
    endTime:                null,
  }
}

function advanceState(
  prev:       HoursState,
  mode:       GameMode,
): HoursState {
  const nextIdx = prev.currentIdx + 1
  const { hour, minute } = initInteractiveTime(mode)
  if (nextIdx >= prev.questions.length) {
    return { ...prev, correctFlashId: null, gameComplete: true, endTime: Date.now() }
  }
  return {
    ...prev,
    currentIdx:             nextIdx,
    correctFlashId:         null,
    wrongId:                null,
    inputValue:             '',
    inputStatus:            'idle',
    interactiveHour:        hour,
    interactiveMinute:      minute,
    interactiveSubmitWrong: false,
  }
}

export function useHours(difficulty: Difficulty, mode: GameMode, isReverse: boolean) {
  const { t: trans } = useTranslation()
  const timeT = trans.hours.time

  const [state, setState] = useState<HoursState>(() => initState(difficulty, mode, isReverse, timeT))

  const resetGame = useCallback(() => {
    setState(initState(difficulty, mode, isReverse, timeT))
  }, [difficulty, mode, isReverse, timeT])

  // Correct-flash → advance after 700 ms
  useEffect(() => {
    if (state.correctFlashId === null) return
    const t = setTimeout(() => {
      setState(prev => advanceState(prev, mode))
    }, 700)
    return () => clearTimeout(t)
  }, [state.correctFlashId, mode])

  // Clear wrong MCQ highlight
  useEffect(() => {
    if (!state.wrongId) return
    const t = setTimeout(() => setState(prev => ({ ...prev, wrongId: null })), 650)
    return () => clearTimeout(t)
  }, [state.wrongId])

  // Clear wrong text-input status
  useEffect(() => {
    if (state.inputStatus !== 'wrong') return
    const t = setTimeout(() => setState(prev => ({ ...prev, inputStatus: 'idle' })), 650)
    return () => clearTimeout(t)
  }, [state.inputStatus])

  // Clear interactive submit-wrong indicator
  useEffect(() => {
    if (!state.interactiveSubmitWrong) return
    const t = setTimeout(() => setState(prev => ({ ...prev, interactiveSubmitWrong: false })), 650)
    return () => clearTimeout(t)
  }, [state.interactiveSubmitWrong])

  // ── Actions ─────────────────────────────────────────────────────

  const selectWrittenOption = useCallback((option: string) => {
    setState(prev => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const q         = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (option === q.written) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: option }
      }
      return { ...prev, moves, startTime, wrongId: option }
    })
  }, [])

  const selectClockOption = useCallback((optionId: string) => {
    setState(prev => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const q        = prev.questions[prev.currentIdx]
      const selected = q.clockOptions.find(o => o.id === optionId)
      if (!selected) return prev
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (timesMatch(selected.time, q.displayTime, mode)) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: optionId }
      }
      return { ...prev, moves, startTime, wrongId: optionId }
    })
  }, [mode])

  const setInputValue = useCallback((val: string) => {
    setState(prev => ({ ...prev, inputValue: val, inputStatus: 'idle' }))
  }, [])

  const submitTextInput = useCallback(() => {
    setState(prev => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const q         = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      if (normalizeWritten(prev.inputValue) === normalizeWritten(q.written)) {
        return {
          ...prev, moves, startTime,
          answered:       prev.answered + 1,
          inputStatus:    'correct' as const,
          correctFlashId: '__text__',
        }
      }
      return { ...prev, moves, startTime, inputStatus: 'wrong' as const }
    })
  }, [])

  const setInteractiveTime = useCallback((hour: number, minute: number) => {
    setState(prev => ({
      ...prev,
      interactiveHour:   hour,
      interactiveMinute: minute as ValidMinute,
    }))
  }, [])

  const submitInteractive = useCallback(() => {
    setState(prev => {
      if (prev.correctFlashId !== null || prev.gameComplete) return prev
      const q         = prev.questions[prev.currentIdx]
      const startTime = prev.startTime ?? Date.now()
      const moves     = prev.moves + 1
      const selected: TimeValue = {
        hour:   prev.interactiveHour,
        minute: prev.interactiveMinute,
      }
      if (timesMatch(selected, q.displayTime, mode)) {
        return { ...prev, moves, startTime, answered: prev.answered + 1, correctFlashId: '__interactive__' }
      }
      return { ...prev, moves, startTime, interactiveSubmitWrong: true }
    })
  }, [mode])

  // Exposed helpers for the interactive drag clock
  const dragHourToAngle = useCallback((angleDeg: number) => {
    setState(prev => {
      const raw = ((angleDeg % 360) + 360) % 360
      // Strip the minute drift so snapping targets integer-hour positions
      const minuteDrift = (prev.interactiveMinute / 60) * 30
      const pureAngle   = ((raw - minuteDrift) + 360) % 360
      const h = snapToNearestHour12((pureAngle / 360) * 12)
      return { ...prev, interactiveHour: h }
    })
  }, [])

  const dragMinuteToAngle = useCallback((angleDeg: number) => {
    const raw = ((angleDeg % 360) + 360) % 360
    const rawMinute = (raw / 360) * 60
    const m = snapToNearestValidMinute(rawMinute)
    setState(prev => ({ ...prev, interactiveMinute: m }))
  }, [])

  const stepHour = useCallback((delta: number) => {
    setState(prev => {
      const max = mode === 'digital' ? 23 : 12
      const min = mode === 'digital' ? 0 : 1
      let h = prev.interactiveHour + delta
      if (h > max) h = min
      if (h < min) h = max
      return { ...prev, interactiveHour: h }
    })
  }, [mode])

  const stepMinute = useCallback((delta: number) => {
    setState(prev => {
      const idx     = VALID_MINUTES.indexOf(prev.interactiveMinute)
      const nextIdx = ((idx + delta) + VALID_MINUTES.length) % VALID_MINUTES.length
      return { ...prev, interactiveMinute: VALID_MINUTES[nextIdx] }
    })
  }, [])

  return {
    state,
    resetGame,
    selectWrittenOption,
    selectClockOption,
    setInputValue,
    submitTextInput,
    setInteractiveTime,
    submitInteractive,
    dragHourToAngle,
    dragMinuteToAngle,
    stepHour,
    stepMinute,
  }
}
